import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AudioWaveform, Laptop, Pause, Play, Smartphone } from 'lucide-react';
import AppStoreBadges from '../components/AppStoreBadges';
import './ReadAlong.css';

/* ------------------------------------------------------------------ *
 * The live demo
 *
 * The passage, the option names and the numbers below all mirror what
 * the app actually does — the colours and alphas are the same values
 * ReadAlongHighlightStyle.swift uses, so the demo is the feature.
 * ------------------------------------------------------------------ */

const PASSAGE = [
  [
    'Squire Trelawney, Dr. Livesey, and the rest of these gentlemen having asked me to write down the whole particulars about Treasure Island, I take up my pen in the year of grace 17__.',
    'I remember him as if it were yesterday, as he came plodding to the inn door, his sea-chest following behind him in a hand-barrow.',
  ],
  [
    'He was a tall, strong, heavy, nut-brown man, his tarry pigtail falling over the shoulders of his soiled blue coat.',
  ],
];

const COLORS = {
  yellow: '#FFCC00',
  orange: '#FF9500',
  green: '#34C759',
  blue: '#007AFF',
  pink: '#FF2D55',
  purple: '#AF52DE',
};

const INTENSITY = {
  subtle: { fill: 0.18, line: 1 },
  medium: { fill: 0.3, line: 2 },
  strong: { fill: 0.45, line: 3 },
};

const WORD_STYLES = [
  ['fill', 'Fill'],
  ['underline', 'Underline'],
  ['both', 'Fill + underline'],
  ['none', 'None'],
];

const CONTEXT_SCOPES = [
  ['none', 'None'],
  ['sentence', 'Sentence'],
  ['paragraph', 'Paragraph'],
];

const INTENSITIES = [
  ['subtle', 'Subtle'],
  ['medium', 'Medium'],
  ['strong', 'Strong'],
];

// Flatten the passage into the word list the ticker walks, keeping each word's
// paragraph and sentence so the tint can follow it.
const WORDS = (() => {
  const out = [];
  PASSAGE.forEach((sentences, p) => {
    sentences.forEach((sentence, s) => {
      sentence.split(/\s+/).forEach((text) => {
        out.push({ text, p, s });
      });
    });
  });
  return out;
})();

// A rough stand-in for narration pace: longer words are spoken for longer.
const durationFor = (word) => Math.min(620, 170 + word.text.length * 34);

const rgba = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

function ReadAlongDemo() {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [onScreen, setOnScreen] = useState(false);
  const [style, setStyle] = useState({
    word: 'both',
    wordColor: 'orange',
    context: 'sentence',
    contextColor: 'yellow',
    intensity: 'medium',
  });

  const panelRef = useRef(null);

  // Only run the ticker while the panel is actually on screen.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), {
      threshold: 0.2,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!playing || !onScreen) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = setTimeout(
      () => setIndex((i) => (i + 1) % WORDS.length),
      durationFor(WORDS[index])
    );
    return () => clearTimeout(timer);
  }, [index, playing, onScreen]);

  const set = useCallback((key, value) => setStyle((s) => ({ ...s, [key]: value })), []);

  const active = WORDS[index];
  const { fill, line } = INTENSITY[style.intensity];

  const contextFill = useMemo(() => rgba(COLORS[style.contextColor], fill * 0.55), [
    style.contextColor,
    fill,
  ]);

  // Word: None draws nothing at all — the tint moves and the text does not.
  // A mark that is drawn must not change the text's metrics either, so the fill
  // bleeds with a box-shadow rather than padding and the weight never changes.
  const wordStyle = useMemo(() => {
    if (style.word === 'none') return undefined;
    const color = COLORS[style.wordColor];
    const wash = rgba(color, fill);
    const filled = style.word !== 'underline';
    return {
      background: filled ? wash : undefined,
      boxShadow: filled ? `0 0 0 2px ${wash}` : undefined,
      borderBottom: style.word === 'fill' ? undefined : `${line}px solid ${color}`,
      color: 'var(--text-primary)',
    };
  }, [style.word, style.wordColor, fill, line]);

  let cursor = -1;

  return (
    <div className="ra-demo" ref={panelRef}>
      <div className="ra-demo-page" aria-hidden="true">
        {PASSAGE.map((sentences, p) => {
          const paragraphTinted = style.context === 'paragraph' && active.p === p;
          return (
            <p
              key={p}
              className="ra-demo-para"
              style={paragraphTinted ? { background: contextFill } : undefined}
            >
              {sentences.map((sentence, s) => {
                const sentenceTinted =
                  style.context === 'sentence' && active.p === p && active.s === s;
                return (
                  <span
                    key={s}
                    className={sentenceTinted ? 'ra-demo-tint' : undefined}
                    style={sentenceTinted ? { background: contextFill } : undefined}
                  >
                    {sentence.split(/\s+/).map((text, w) => {
                      cursor += 1;
                      const marked = cursor === index && wordStyle !== undefined;
                      return (
                        <React.Fragment key={w}>
                          <span
                            className={marked ? 'ra-demo-word' : undefined}
                            style={marked ? wordStyle : undefined}
                          >
                            {text}
                          </span>{' '}
                        </React.Fragment>
                      );
                    })}
                  </span>
                );
              })}
            </p>
          );
        })}
      </div>

      <div className="ra-demo-controls">
        <div className="ra-demo-controls-head">
          <span>Reader Settings &rarr; Read Along Highlight</span>
          <button
            type="button"
            className="ra-demo-play"
            onClick={() => setPlaying((p) => !p)}
            aria-label={playing ? 'Pause the demo' : 'Play the demo'}
          >
            {playing ? <Pause size={13} /> : <Play size={13} />}
            {playing ? 'Pause' : 'Play'}
          </button>
        </div>

        <Segmented
          label="Word"
          options={WORD_STYLES}
          value={style.word}
          onChange={(v) => set('word', v)}
        />
        {style.word !== 'none' && (
          <Swatches
            label="Word colour"
            value={style.wordColor}
            onChange={(v) => set('wordColor', v)}
          />
        )}
        <Segmented
          label="Context"
          options={CONTEXT_SCOPES}
          value={style.context}
          onChange={(v) => set('context', v)}
        />
        {style.context !== 'none' && (
          <Swatches
            label="Context colour"
            value={style.contextColor}
            onChange={(v) => set('contextColor', v)}
          />
        )}
        <Segmented
          label="Intensity"
          options={INTENSITIES}
          value={style.intensity}
          onChange={(v) => set('intensity', v)}
        />
      </div>
    </div>
  );
}

function Segmented({ label, options, value, onChange }) {
  return (
    <div className="ra-demo-row">
      <span className="ra-demo-label">{label}</span>
      <div className="ra-demo-seg" role="group" aria-label={label}>
        {options.map(([key, name]) => (
          <button
            key={key}
            type="button"
            className={value === key ? 'is-on' : undefined}
            aria-pressed={value === key}
            onClick={() => onChange(key)}
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
}

function Swatches({ label, value, onChange }) {
  return (
    <div className="ra-demo-row">
      <span className="ra-demo-label">{label}</span>
      <div className="ra-demo-swatches" role="group" aria-label={label}>
        {Object.entries(COLORS).map(([key, hex]) => (
          <button
            key={key}
            type="button"
            className={`ra-demo-swatch${value === key ? ' is-on' : ''}`}
            style={{ background: hex }}
            aria-pressed={value === key}
            aria-label={key}
            onClick={() => onChange(key)}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The post
 * ------------------------------------------------------------------ */

function ReadAlong() {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('ra-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.ra-reveal').forEach((el) => observerRef.current.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="readalong-page">
      <Helmet>
        <title>Read Along - Audra Books | Synced Narration for Audiobookshelf</title>
        <meta
          name="description"
          content="Read Along highlights the word your narrator is speaking, in your own ebook, aligned on your own device. Any reader can align a book; a Mac aligns a whole library; publish the result to your Audiobookshelf server and every reader on it gets it for free."
        />
        <link rel="canonical" href="https://audrabooks.app/read-along" />
        <meta property="og:title" content="Read Along - Audra Books" />
        <meta
          property="og:description"
          content="Synced narration for your own Audiobookshelf library. Aligned on your device, shared through your server."
        />
        <meta property="og:url" content="https://audrabooks.app/read-along" />
      </Helmet>

      <article className="ra-post">
        {/* Masthead */}
        <header className="ra-head">
          <div className="ra-col ra-reveal">
            <span className="ra-kicker">
              <AudioWaveform size={14} />
              Engineering &middot; Read Along
            </span>
            <h1>
              The narrator reads.
              <br />
              <span className="text-gradient">The words light up.</span>
            </h1>
            <p className="ra-standfirst">
              How Audra aligns your own audiobook to your own ebook, on your own device — and why
              the result is worth handing to everybody else on your server.
            </p>
            <p className="ra-byline">September 2026 &middot; 5 min read</p>
          </div>
        </header>

        {/* Lead */}
        <section className="ra-body">
          <div className="ra-col ra-reveal">
            <p className="ra-lead">
              Read Along follows an audiobook through the matching ebook, one word at a time. There
              is no special edition to buy and no service to sign up for: the alignment is computed
              from the files already sitting in your Audiobookshelf library.
            </p>
            <p>
              Three things are worth knowing before the details. <strong>Anyone can do it</strong> —
              a single reader on a single phone can align the book they are holding, chapter by
              chapter, and start following the narration inside a minute.{' '}
              <strong>Hardware decides how long it takes</strong> — an M-series Mac gets through a
              book roughly ten times faster than an iPhone does, which is the difference between a
              library in an afternoon and a library over a month of evenings. And{' '}
              <strong>the work only has to be done once</strong>: a server owner can publish every
              finished alignment back to Audiobookshelf, where every other reader picks it up
              automatically with nothing to compute and nothing to download.
            </p>
          </div>
        </section>

        {/* The demo */}
        <section className="ra-body">
          <div className="ra-col ra-wide ra-reveal">
            <ReadAlongDemo />
            <p className="ra-caption">
              The real thing, with the real controls. Every option here is a row in Reader Settings
              &rarr; Read Along Highlight, and it changes the reader the moment you pick it.
            </p>
          </div>
        </section>

        {/* 01 */}
        <section className="ra-body">
          <div className="ra-col ra-reveal">
            <h2>
              <span className="ra-num">01</span>In the reader
            </h2>
            <p>
              Every word in the map carries its own start time and duration, so the mark lands on
              the word being spoken rather than drifting a line behind the voice. A softer wash sits
              behind the sentence or the paragraph around it, fading in over two seconds so it never
              flickers, and it stays put through the small silences — an unnarrated heading or a
              publisher's intro leaves the page clean rather than highlighting a guess.
            </p>
            <p>
              The page turns itself when the narration crosses onto the next one. Scroll away to
              read ahead and a <em>Back to narration</em> pill appears to bring you home. Found a
              paragraph you would rather hear? Double tap any word and the audiobook seeks to it; a
              single tap still turns the page. The Read Along sheet carries the transport and the
              speed menu, so pausing or slowing the narrator never means leaving the text.
            </p>
            <p>
              One glyph ties it together. A small waveform beside <strong>Ebook</strong> on the book
              page means a finished alignment is already on this device; the same waveform sits in
              the reader's bottom bar to open the sheet, and fills with your accent colour while
              Read Along is following. Your on-off choice is remembered per book.
            </p>
          </div>
        </section>

        {/* 02 */}
        <section className="ra-body ra-body-alt">
          <div className="ra-col ra-reveal">
            <h2>
              <span className="ra-num">02</span>How the alignment is made
            </h2>
            <p>
              No cloud service, no companion app, no change to your Audiobookshelf server. Four
              steps, all of them on the device in your hand.
            </p>
            <ol className="ra-steps">
              <li>
                <strong>The chapters are paired.</strong> The audiobook's chapter list is matched to
                the ebook's table of contents so each chapter can be aligned on its own. That
                pairing is what makes the whole thing resumable: finish a chapter, keep a chapter.
              </li>
              <li>
                <strong>The audio is transcribed on device.</strong> Apple's on-device speech
                recognition listens to one chapter at a time and returns a timestamp for every word
                it hears. The audio never leaves your device and the transcript is thrown away as
                soon as it has been used — the only thing that ever goes anywhere is the finished
                map, and only if you ask for it.
              </li>
              <li>
                <strong>The transcript is matched to your ebook.</strong> A pure text aligner
                anchors what was heard against the EPUB's own words, tolerating the narrator's slips
                and the typesetter's spellings and interpolating across short unmatched runs. A
                chapter of <em>Treasure Island</em> matched 97.4% of the ebook's words exactly, and
                correctly matched nothing at all against the 85-second publisher's intro.
              </li>
              <li>
                <strong>
                  A <code>.readalong</code> file is saved.
                </strong>{' '}
                One small compressed file per book, holding word positions and timings — not your
                book's text, not the audio. A 200,000-word map encodes to about 22 KB, which is why
                sharing them costs a server essentially nothing.
              </li>
            </ol>
            <p>
              All of this assumes the ebook and the audiobook are the same edition. Audra watches
              the match rate as it works, and if the two texts simply do not correspond it stops,
              deletes the partial result and says so, rather than leaving you with a highlight that
              wanders. An abridged audiobook against a full text is the usual culprit.
            </p>
          </div>
        </section>

        {/* 03 */}
        <section className="ra-body">
          <div className="ra-col ra-reveal">
            <h2>
              <span className="ra-num">03</span>What it costs in time
            </h2>
            <p>
              Aligning is real work — the audio has to be listened to — and how fast that goes is
              decided almost entirely by the silicon doing the listening.
            </p>
          </div>

          <div className="ra-col ra-wide ra-reveal">
            <table className="ra-table">
              <thead>
                <tr>
                  <th scope="col">Same book, same work</th>
                  <th scope="col">
                    <Smartphone size={15} /> iPhone 15 Pro
                  </th>
                  <th scope="col" className="ra-table-featured">
                    <Laptop size={15} /> M4 Pro Mac
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Chapters transcribed at once</th>
                  <td>1</td>
                  <td className="ra-table-featured">up to 8</td>
                </tr>
                <tr>
                  <th scope="row">One 15-minute chapter</th>
                  <td>17&ndash;45 seconds</td>
                  <td className="ra-table-featured">8&ndash;14 seconds</td>
                </tr>
                <tr>
                  <th scope="row">Throughput while transcribing</th>
                  <td>18&ndash;52&times; realtime</td>
                  <td className="ra-table-featured">68&times; on one session, 288&times; on eight</td>
                </tr>
                <tr>
                  <th scope="row">
                    A 6.7-hour audiobook<span className="ra-mark">*</span>
                  </th>
                  <td>~25 minutes</td>
                  <td className="ra-table-featured">~3 minutes</td>
                </tr>
                <tr>
                  <th scope="row">
                    A 12.5-hour audiobook<span className="ra-mark">*</span>
                  </th>
                  <td>~45 minutes</td>
                  <td className="ra-table-featured">~6 minutes</td>
                </tr>
              </tbody>
            </table>
            <p className="ra-caption">
              <span className="ra-mark">*</span> The 6.7-hour iPhone figure is a timed run — 40
              chapters, 61,448 words, start to finish. The other whole-book numbers are arithmetic
              from the measured per-chapter throughput above, not timed runs of their own.
            </p>
          </div>

          <div className="ra-col ra-reveal">
            <p>
              The gap is concurrency, not clock speed. A Mac transcribes on half its active cores,
              up to eight chapters at a time; a phone or an iPad runs one. Audra is a "Designed for
              iPhone &amp; iPad" app, so it installs and runs on any Apple Silicon Mac straight from
              the App Store — sign in as the same user, point it at the same server, and it produces
              the same file in the same format, only far sooner.
            </p>
            <p>
              You never have to sit and watch a phone finish, either. The chapter you are reading is
              aligned first and the rest queues behind it, each one saved the moment it is done, so
              you can read chapter one while chapter two aligns, pause and come back tonight, or add
              a few chapters at a time to a book that stopped short.
            </p>
            <p>
              Both machines want to stay awake. iOS will not run speech recognition in the
              background at all: send the app away mid-chapter and the row reads{' '}
              <em>Paused — will continue when the app is open</em>, then resumes from the last
              finished chapter when you return. On a Mac, Audra holds off the display sleep, but one
              that follows its Energy settings into full sleep stalls the chapter in flight; that
              book is marked and the run carries on with the next.
            </p>
          </div>
        </section>

        {/* 04 */}
        <section className="ra-body ra-body-alt">
          <div className="ra-col ra-reveal">
            <h2>
              <span className="ra-num">04</span>Align all, for the whole shelf
            </h2>
            <p>
              Settings &rarr; Read Along &rarr; <strong>Align all</strong> is the same engine
              pointed at everything. The conservative pass touches only the hybrids whose
              audiobooks are already downloaded and leaves your downloads exactly as it found them.
              The librarian's pass — <strong>download, align, and remove</strong> — is the reason a
              Mac can align a library it could never store: Audra quotes the total download size up
              front, then works one book at a time, fetching the audiobook, aligning it, publishing
              the map and deleting the download before it moves on.
            </p>
            <p className="ra-flow" aria-hidden="true">
              <span>Ask the server</span>
              <span>Download one book</span>
              <span>Align it</span>
              <span>Publish the map</span>
              <span>Delete the download</span>
              <span className="ra-flow-loop">Next book</span>
            </p>
            <p>
              That first step matters most. Before spending a gigabyte, Audra asks whether an
              alignment for the book already exists on your server; if it does, the book is marked
              done and nothing is downloaded at all. Disk use stays flat at one audiobook whatever
              the size of the library, a book you had downloaded yourself is never cleaned up, and a
              single unalignable book is marked and stepped over rather than halting the run — only
              genuinely running out of space stops it. Progress reads{' '}
              <em>Book 12 of 340 · Aligning</em>, and the dialog says up front how many finished or
              known-bad books it is skipping. Force-quit the app and you lose only the book in
              flight; press Align all again and everything already done is skipped.
            </p>
          </div>
        </section>

        {/* 05 */}
        <section className="ra-body">
          <div className="ra-col ra-reveal">
            <h2>
              <span className="ra-num">05</span>Publish once, and everyone reads
            </h2>
            <p>
              An alignment is a fact about two files, not about a person. Once somebody has computed
              it, nobody else ever needs to — which is the whole argument for the fastest machine on
              a server doing the work for the rest.
            </p>
            <p>
              With <strong>Publish to server</strong> switched on, a finished map is written into
              the book's own folder as a sidecar beside the ebook:{' '}
              <code>Treasure_Island.epub</code> gets <code>Treasure_Island.readalong</code>. It goes
              up through Audiobookshelf's ordinary upload API — no plugin, no schema change, no
              patched server, tested against Audiobookshelf 2.36.0 as shipped. Admins trigger a
              library scan so the file registers immediately; everyone else waits for the next scan,
              and the app says so in the meantime.
            </p>
            <p>
              Anyone with access to that library then picks the sidecar up the first time they open
              the book, or for every hybrid at once from Settings &rarr; Read Along. They tap Read
              Along and it works: no transcription, no audiobook download, no waiting, streaming-only
              listeners included. A downloaded map is checked against the audio's duration and the
              ebook's own fingerprint before it is trusted, so a different copy of the ebook falls
              back to unaligned instead of highlighting the wrong words. Failures travel too — a
              book that genuinely cannot be aligned gets a small marker recording why, on what
              device and when, so everyone else's Align all skips it and the server learns from one
              person's wasted half hour.
            </p>
            <p className="ra-aside">
              <strong>When there are two maps.</strong> No local map, or an empty one, uses the
              server's. An unfinished local one defers to the server's if it covers at least as many
              chapters. A finished map you made yourself is kept — the server never overwrites your
              own completed work — while one that came from the server is replaced when a newer one
              is published. Holding a backlog from before publishing existed? Settings &rarr; Read
              Along offers <em>Publish N to server</em> and sends the lot in one pass.
            </p>
          </div>
        </section>

        {/* 06 */}
        <section className="ra-body ra-body-alt">
          <div className="ra-col ra-reveal">
            <h2>
              <span className="ra-num">06</span>What it needs
            </h2>
            <p>
              A hybrid item — one Audiobookshelf library item holding both an EPUB and the
              audiobook; Settings &rarr; Read Along lists every one in your library so you never
              have to guess. Matching editions, since abridgements and swapped editions are detected
              and refused. A supported language, chosen from the book's own metadata and then your
              device's, and reported before any work starts if no on-device model exists for it. The
              audiobook downloaded, but only to <em>make</em> an alignment — using one published to
              your server needs nothing on disk at all. Upload rights, again only to publish;
              reading a published map needs no more than library access.
            </p>
            <p>
              Read Along, per-book alignment and Align all are <strong>Audra Pro</strong> features.
              They ship on iPhone, iPad and Apple Silicon Macs, with Android to follow.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="ra-cta">
          <div className="ra-col ra-reveal">
            <h2>Hear it and read it at once</h2>
            <p>
              On the library you already own, from the files you already have. Nothing to upload,
              nothing to subscribe to beyond the app itself.
            </p>
            <AppStoreBadges />
            <p className="ra-cta-note">
              Curious about the rest? <Link to="/features">See every feature</Link> or read the{' '}
              <Link to="/releases">release notes</Link>.
            </p>
          </div>
        </section>
      </article>
    </div>
  );
}

export default ReadAlong;
